import { useBandContext } from "Bands/contexts";
import { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { copy } from "shared/assets";
import { Band } from "shared/assets/icons";
import { FormBuilder, Modal } from "shared/components";
import { IAPIState, IFormButtons, IStatus } from "shared/types";
import {
  dataMetaSelector,
  dispatchGetUserVenues,
  editVenueBands,
} from "Venues/state";

import { size } from "lodash-es";
import { addTemporaryToast } from "shared/components/Toast";
import { API_ENDPOINTS } from "shared/constants";
import { IVenue, VENUE_ACTIONS } from "Venues/types";
import { useClearActionMetaOnUnMount } from "Venues/utils";
import { getFields } from "./formData";

interface VenueBandsModalProps {
  venue: IVenue;
  handleClose: () => void;
}

export const VenueBandsModal: FC<VenueBandsModalProps> = ({
  venue,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const { api: editMeta } = useSelector(
    dataMetaSelector(VENUE_ACTIONS.editVenueBands)
  );
  const { userBands } = useBandContext();

  const venueBandIds = useMemo(
    () => venue.bands?.map((band) => band.id),
    [venue.bands]
  );

  const userBandIds = useMemo(() => userBands.map(({ id }) => id), [userBands]);

  useClearActionMetaOnUnMount(VENUE_ACTIONS.editVenueBands);

  const buttons: IFormButtons = {
    primary: {
      children: "Submit",
      size: "m",
      "data-testid": "save_venue_bands",
    },
  };

  const onSubmit = ({ bands }: { bands: string[] }) => {
    dispatch(
      editVenueBands({
        venueId: venue.id,
        bands: size(bands) ? bands.map((id) => API_ENDPOINTS.BAND(id)) : [],
      })
    );
  };

  const title = useMemo(() => venue?.title || "", [venue]);

  const defaultValues = useMemo(
    () => ({
      bands: userBandIds?.filter((id) =>
        venueBandIds?.includes(API_ENDPOINTS.BAND(id))
      ),
    }),
    [venueBandIds, userBandIds]
  );

  useEffect(() => {
    if (editMeta?.status === IAPIState.succeeded) {
      dispatchGetUserVenues(dispatch);

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: copy.venueBlock.createVenue.messages.successEdit,
        })
      );

      handleClose();
    }
  }, [editMeta, venue.id, dispatch, handleClose, venueBandIds]);

  return (
    <Modal
      title={`${copy.venueBlock.createVenue.edit} ${title}`}
      icon={Band}
      handleClose={handleClose}
    >
      <FormBuilder
        fields={getFields({
          userBands,
          venueBandIds,
          bandCount: venue?.bandCount || undefined,
        })}
        submitMode={"isDirty"}
        defaultValues={defaultValues}
        buttons={buttons}
        onSubmit={onSubmit}
        isSubmitting={editMeta?.status === IAPIState.pending}
        apiErrorMessage={!!editMeta?.error}
      />
    </Modal>
  );
};
